import styled, { css } from "styled-components";
import { Flex } from "rebass/styled-components";
import { Link } from "react-router-dom";

/**
 * Trigger and panel share a width and a horizontal inset, which is what lines
 * the panel up under the avatar instead of hanging off a narrower control.
 */
/**
 * The panel's width. The trigger sizes to its own content and is capped at this
 * instead: at a flat 360px the avatar, name and caret left roughly 160px of
 * dead space, which showed up as an oddly wide hover and open state. Both are
 * right-anchored, so their right edges still line up.
 */
const MENU_WIDTH = "360px";
const MENU_INSET = "12px";

export const Cluster = styled(Flex)`
  align-items: center;
  gap: 12px;
  position: relative;
`;

/**
 * Site accent, not the Unlimited tier's purple. It wore the tier's own colours
 * to read as the thing it sells, but in a strip that already holds a search
 * pill, ghost links, a bell and an avatar, a saturated gradient in a fifth
 * colour was the loudest thing in the row without being the most important.
 * Same treatment as the mega menu CTA, so the two agree.
 */
export const TrialButton = styled.button`
  /* The Unlimited tier's own purple, so the button looks like the thing it
     sells rather than another green CTA. Flat rather than the gradient and glow
     it had: in a strip of ghost links and a search field, that read as the
     loudest element on the page. */
  align-items: center;
  background: ${({ theme }) => theme.colors.tierPlat};
  border: 1px solid transparent;
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  display: flex;
  gap: 6px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  /* Sits apart from the bell and avatar, which belong together. */
  margin-right: 8px;
  padding: 5px 11px;
  white-space: nowrap;

  transition: background 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.tierPlatDark};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.tierPlat};
    outline-offset: 2px;
  }

  svg {
    height: 14px;
    width: 14px;
  }

  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    display: none;
  }
`;

/**
 * The strip's own auth pair, rather than the site's generic Button. That
 * component is sized for page bodies and read heavy beside ghost links, a
 * search pill and an avatar.
 *
 * The outline keeps the tertiary Button's treatment, envy border and text that
 * fills on hover, at the strip's size. Padding is 2px lighter than the solid
 * one to absorb the border, so the pair matches in height.
 */
export const AuthSecondary = styled(Link)`
  align-items: center;
  background: transparent;
  border: 2px solid ${({ theme }) => theme.colors.envy};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.envy};
  display: flex;
  font-size: 13px;
  font-weight: 600;
  padding: 5px 11px;
  text-decoration: none;
  transition:
    background 0.2s ease,
    color 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: ${({ theme }) => theme.colors.envy};
    color: ${({ theme }) => theme.colors.ice};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 2px;
  }
`;

export const AuthPrimary = styled(Link)`
  align-items: center;
  background: ${({ theme }) => theme.colors.envy};
  border: 2px solid transparent;
  border-radius: 8px;
  display: flex;
  color: ${({ theme }) => theme.colors.ice};
  font-size: 13px;
  font-weight: 600;
  padding: 5px 11px;
  text-decoration: none;
  transition: background 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: ${({ theme }) => theme.colors.darkerEnvy};
    color: ${({ theme }) => theme.colors.ice};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 2px;
  }
`;

/* Trigger */

/**
 * A compact control, not the panel's lid. It used to be square-topped and
 * stretched to the bar's bottom edge so it read as the panel's header, but that
 * geometry was tuned to the bar row's 18px padding. In the strip there is no
 * row to be flush with, so it is a normal rounded button and the panel below is
 * a normal dropdown.
 */
export const PillTrigger = styled.button<{ $open?: boolean }>`
  align-items: center;
  background: ${({ theme, $open }) =>
    $open ? theme.colors.chalkAlpha08 : "transparent"};
  border: none;
  border-radius: 8px;
  box-sizing: border-box;
  cursor: pointer;
  display: flex;
  gap: 8px;
  max-width: ${MENU_WIDTH};
  padding: 5px ${MENU_INSET};

  &:hover {
    background: ${({ theme }) => theme.colors.chalkAlpha06};
  }

  &:hover span,
  &:hover svg {
    color: ${({ theme }) => theme.colors.ice};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 2px;
  }

  /* PillLabel is hidden here, so the fixed width would be 200px of empty space
     stranding the caret at the screen edge. */
  @media (max-width: ${({ theme }) => theme.breaks.small}) {
    gap: 4px;
    padding-left: 8px;
    padding-right: 8px;
    width: auto;
  }
`;

export const PillLabel = styled.span`
  flex: 1;
  min-width: 0;
  text-align: left;

  @media (max-width: ${({ theme }) => theme.breaks.small}) {
    display: none;
  }
`;

// Tier gradients are set on a Text with its own sizing, so shrink it here.
export const PillTier = styled.span`
  display: block;

  * {
    font-size: 13px;
  }
`;

// Sits on the tier's line, so it matches the tier's size rather than the name's.
export const PillStatus = styled.span`
  color: ${({ theme }) => theme.colors.envy};
  display: block;
  font-size: 13px;
  font-weight: 600;
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const PillUsername = styled.span`
  color: ${({ theme }) => theme.colors.ice};
  display: block;
  font-size: 16px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const PillCaret = styled.span<{ $open?: boolean }>`
  align-items: center;
  color: ${({ theme, $open }) =>
    $open ? theme.colors.ice : theme.colors.chalk};
  display: flex;
  flex-shrink: 0;

  svg {
    height: 16px;
    transform: rotate(${({ $open }) => ($open ? "180deg" : "0deg")});
    transition: transform 150ms ease;
    width: 16px;
  }
`;

/* Panel */

export const MenuPanel = styled.div`
  background: ${({ theme }) => theme.colors.navy500};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha10};
  border-radius: 12px;
  box-shadow: 0px 12px 24px rgba(0, 0, 0, 0.35);
  box-sizing: border-box;
  margin-top: 8px;
  padding: ${MENU_INSET};
  /* Below the nav bar, so it never runs past a short laptop screen. */
  max-height: calc(100vh - 80px);
  overflow-y: auto;
  position: absolute;
  right: 0;
  top: 100%;
  width: ${MENU_WIDTH};

  @media (max-width: ${({ theme }) => theme.breaks.small}) {
    width: min(${MENU_WIDTH}, calc(100vw - 24px));
  }
  z-index: 120;

  @media (prefers-reduced-motion: no-preference) {
    animation: userMenuIn 120ms ease-out;
  }

  @keyframes userMenuIn {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
  }
`;

// ClipQuotaBar renders nothing for unlimited plans, so this collapses cleanly.
export const QuotaSlot = styled.div`
  &:not(:empty) {
    margin-bottom: 12px;
  }
`;

// Plain rows like the settings group: the setup checklist is the panel's only
// card, so nothing else competes with it.
export const PersonalRows = styled.div`
  display: flex;
  flex-direction: column;
`;

/**
 * Outdented to the panel's own edge rather than lining up with the rows it
 * labels, so the card below reads as sitting under it rather than beside it.
 */
export const SectionLabel = styled.div`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.6px;
  padding: 10px 0 6px;
  text-transform: uppercase;
`;

// The link fills the row and the pin sits beside it: a button inside an anchor
// is not valid, and nesting one swallows the navigation.
export const ShortcutRow = styled.div`
  align-items: center;
  display: flex;

  a {
    flex: 1;
    min-width: 0;
  }
`;

export const PinButton = styled.button<{ $pinned: boolean }>`
  align-items: center;
  background: none;
  border: none;
  color: ${({ theme, $pinned }) =>
    $pinned ? theme.colors.ice : theme.colors.chalk};
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  margin-right: 10px;
  padding: 6px;
  border-radius: 6px;

  &:hover {
    background: ${({ theme }) => theme.colors.chalkAlpha10};
    color: ${({ theme }) => theme.colors.ice};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: -2px;
  }

  svg {
    height: 14px;
    width: 14px;
  }
`;

export const MenuList = styled.div`
  display: flex;
  flex-direction: column;
`;

// border-box: links default to content-box, so width: 100% plus the padding
// pushed their hover fill 24px past the panel.
const rowStyle = css`
  align-items: center;
  background: none;
  border: none;
  border-radius: 8px;
  box-sizing: border-box;
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  display: flex;
  font-family: inherit;
  font-size: 16px;
  gap: 14px;
  padding: 9px 12px;
  text-align: left;
  text-decoration: none;
  width: 100%;

  &:hover {
    background: ${({ theme }) => theme.colors.chalkAlpha06};
    color: ${({ theme }) => theme.colors.envy};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: -2px;
  }
`;

export const MenuLink = styled(Link)`
  ${rowStyle}
`;

export const MenuAction = styled.button`
  ${rowStyle}
`;

export const MenuIcon = styled.span`
  align-items: center;
  color: ${({ theme }) => theme.colors.chalk};
  display: flex;
  flex-shrink: 0;

  /* Bundled SVG assets carry no fill of their own, so without this they render
     black and vanish on the dark panel. No-op for react-icons glyphs. */
  svg {
    fill: currentColor;
    height: 20px;
    width: 20px;
  }
`;

export const MenuLabel = styled.span`
  flex: 1;
  min-width: 0;
`;

export const MenuNewBadge = styled.span`
  background: ${({ theme }) => theme.colors.alert};
  border-radius: 12px;
  color: ${({ theme }) => theme.colors.ice};
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  line-height: 16px;
  padding: 2px 6px;
  text-transform: uppercase;
`;

// Nudged inboard of the row's own padding: hard against the panel edge these
// right-aligned values read as falling off it.
export const MenuCount = styled.span`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 14px;
  margin-right: 6px;
  font-variant-numeric: tabular-nums;
`;

export const MenuPending = styled.span`
  color: ${({ theme }) => theme.colors.envy};
  font-size: 14px;
  margin-right: 6px;
  font-weight: 600;
  white-space: nowrap;
`;

/* Setup checklist */

export const AvatarSlot = styled.span`
  display: flex;
  flex-shrink: 0;
  position: relative;
`;

// Red like the bell and draft dots: this is the site's "needs you" colour. It
// pings a few times on load, then holds still.
export const AvatarDot = styled.span`
  background: ${({ theme }) => theme.colors.alert};
  border: 2px solid ${({ theme }) => theme.colors.navy600};
  border-radius: 50%;
  height: 12px;
  position: absolute;
  right: -3px;
  top: -3px;
  width: 12px;

  @media (prefers-reduced-motion: no-preference) {
    animation: setupDotPing 1.6s ease-out 0.6s 3;
  }

  @keyframes setupDotPing {
    0% {
      box-shadow: 0 0 0 0 ${({ theme }) => theme.colors.alertAlpha80};
    }
    70%,
    100% {
      box-shadow: 0 0 0 7px transparent;
    }
  }
`;

// A checklist in miniature: done segments fill green, what's left counts in
// red beside them.
export const SetupLine = styled(PillStatus)`
  align-items: center;
  color: ${({ theme }) => theme.colors.chalk};
  display: flex;
  font-weight: 500;
  gap: 6px;
`;

export const SetupMiniMeter = styled.span`
  display: flex;
  flex-shrink: 0;
  gap: 2px;
`;

export const SetupMiniSegment = styled.span<{ $filled: boolean }>`
  background: ${({ theme, $filled }) =>
    $filled ? theme.colors.envy : theme.colors.chalkAlpha40};
  border-radius: 999px;
  height: 4px;
  width: 9px;
`;

export const SetupProgress = styled.b`
  color: ${({ theme }) => theme.colors.alert};
  font-variant-numeric: tabular-nums;
  font-weight: 700;
`;

export const SetupCard = styled.section`
  background: ${({ theme }) => theme.colors.navy};
  border: 1px solid ${({ theme }) => theme.colors.navy50};
  border-radius: 8px;
  margin-bottom: 8px;
`;

export const SetupHeader = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  padding: 12px 12px 8px;
`;

export const SetupTitle = styled.span`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 16px;
  font-weight: 700;
`;

export const SetupCount = styled.span`
  background: ${({ theme }) => theme.colors.alertAlpha10};
  border-radius: 999px;
  color: ${({ theme }) => theme.colors.alert};
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  font-weight: 700;
  padding: 2px 8px;
`;

export const SetupMeter = styled.div`
  display: flex;
  gap: 3px;
  padding: 0 12px 4px;
`;

export const SetupSegment = styled.span<{ $filled: boolean }>`
  background: ${({ theme, $filled }) =>
    $filled ? theme.colors.envy : theme.colors.chalkAlpha20};
  border-radius: 999px;
  flex: 1;
  height: 3px;
`;

export const SetupRows = styled.ol`
  list-style: none;
  margin: 0;
  padding: 4px 0;
`;

// The one green thing in the panel: the step to do now.
export const SetupNextAction = styled.span`
  background: ${({ theme }) => theme.colors.envy};
  border-radius: 999px;
  color: ${({ theme }) => theme.colors.ice};
  font-size: 13px;
  font-weight: 700;
  margin-right: 6px;
  padding: 4px 12px;
  white-space: nowrap;
`;

export const SetupAction = styled(MenuCount)`
  font-weight: 600;
  white-space: nowrap;
`;

export const SetupRowText = styled.span`
  display: flex;
  flex: 1;
  flex-direction: column;
  line-height: 20px;
  min-width: 0;
`;

// Wraps rather than truncating: the action pill leaves little room.
export const SetupBenefit = styled.span`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 13px;
  line-height: 18px;
`;

// A step waiting on another: readable, just not clickable.
export const SetupRowBlocked = styled.div`
  ${rowStyle}
  color: ${({ theme }) => theme.colors.chalk};
  cursor: default;

  &:hover {
    background: none;
    color: ${({ theme }) => theme.colors.chalk};
  }
`;

export const SetupFooter = styled.div`
  align-items: center;
  border-top: 1px solid ${({ theme }) => theme.colors.chalkAlpha10};
  color: ${({ theme }) => theme.colors.chalk};
  display: flex;
  font-size: 13px;
  gap: 10px;
  padding: 8px 12px;
`;

export const SetupDoneSummary = styled.span`
  align-items: center;
  display: flex;
  flex: 1;
  gap: 6px;
  min-width: 0;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  svg {
    flex-shrink: 0;
    height: 14px;
    width: 14px;
  }
`;

export const SetupHide = styled.button`
  background: none;
  border: none;
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.chalk};
  cursor: pointer;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  margin: -2px -6px -2px auto;
  padding: 2px 6px;
  transition: color 0.15s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.ice};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 2px;
  }
`;

export const PlatformIcons = styled.span`
  display: inline-flex;
  gap: 4px;
  margin-right: 6px;
  vertical-align: -2px;

  svg {
    height: 13px;
    width: 13px;
  }
`;

export const MenuDivider = styled.div`
  background: ${({ theme }) => theme.colors.chalkAlpha10};
  height: 1px;
  margin: 12px 0;
`;

export const MenuPlanFooter = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.chalkAlpha10};
  margin-top: 10px;
  padding-top: 12px;
`;

// Flat on purpose: a glowing gradient here outshouted the setup checklist.
export const MenuPlanCta = styled(Link)`
  background: ${({ theme }) => theme.colors.navy};
  border: 1px solid ${({ theme }) => theme.colors.navy50};
  border-radius: 8px;
  box-sizing: border-box;
  color: ${({ theme }) => theme.colors.ice};
  display: block;
  font-size: 14px;
  font-weight: 600;
  padding: 10px;
  text-align: center;
  text-decoration: none;
  transition: background 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.navy50};
    color: ${({ theme }) => theme.colors.ice};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 2px;
  }
`;
